import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Save, RotateCcw } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/top-bar")({
  component: TopBarAdmin,
});

export type TopBarSettings = {
  enabled: boolean;
  phone: string;
  email: string;
  promo_text: string;
  promo_cta_label: string;
  promo_cta_to: string;
  support_label: string;
  support_to: string;
  cta_label: string;
  cta_to: string;
  language_label: string;
};

const DEFAULT: TopBarSettings = {
  enabled: true,
  phone: "+966 55 975 8082",
  email: "hello@coderlity.com",
  promo_text: "New: AI Automation Suite is live —",
  promo_cta_label: "Explore",
  promo_cta_to: "/services",
  support_label: "Support",
  support_to: "/support",
  cta_label: "Get a quote",
  cta_to: "/contact",
  language_label: "EN",
};

function TopBarAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["site_settings", "top_bar"],
    queryFn: async (): Promise<TopBarSettings> => {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "top_bar")
        .maybeSingle();
      return { ...DEFAULT, ...((data?.value as TopBarSettings) ?? {}) };
    },
  });

  const [state, setState] = useState<TopBarSettings>(DEFAULT);
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (data) {
      setState(data);
      setDirty(false);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async (value: TopBarSettings) => {
      const { error } = await supabase
        .from("site_settings")
        .upsert({ key: "top_bar", value });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Top bar saved");
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["site_settings", "top_bar"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  const upd = <K extends keyof TopBarSettings>(k: K, v: TopBarSettings[K]) => {
    setState((s) => ({ ...s, [k]: v }));
    setDirty(true);
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Top bar"
        description="Edit the announcement bar shown above the header — contact details, promo, and CTAs."
      />

      <Card>
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 p-3">
            <div>
              <Label className="text-sm">Show top bar</Label>
              <p className="text-xs text-muted-foreground">Hide it sitewide if not needed.</p>
            </div>
            <Switch checked={state.enabled} onCheckedChange={(v) => upd("enabled", v)} />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Phone (tel:)" value={state.phone} onChange={(v) => upd("phone", v)} />
            <Field label="Email (mailto:)" value={state.email} onChange={(v) => upd("email", v)} />
          </div>

          <div className="grid gap-4">
            <Field label="Promo text" value={state.promo_text} onChange={(v) => upd("promo_text", v)} />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Promo CTA label" value={state.promo_cta_label} onChange={(v) => upd("promo_cta_label", v)} />
              <Field label="Promo CTA link" value={state.promo_cta_to} onChange={(v) => upd("promo_cta_to", v)} />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Support label" value={state.support_label} onChange={(v) => upd("support_label", v)} />
            <Field label="Support link" value={state.support_to} onChange={(v) => upd("support_to", v)} />
            <Field label="Primary CTA label" value={state.cta_label} onChange={(v) => upd("cta_label", v)} />
            <Field label="Primary CTA link" value={state.cta_to} onChange={(v) => upd("cta_to", v)} />
            <Field label="Language label" value={state.language_label} onChange={(v) => upd("language_label", v)} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost"
              disabled={!dirty || !data}
              onClick={() => data && (setState(data), setDirty(false))}
            >
              <RotateCcw className="h-4 w-4" /> Revert
            </Button>
            <Button onClick={() => save.mutate(state)} disabled={!dirty || save.isPending}>
              <Save className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
